import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { useTenant } from '../../context/TenantContext';
import { getTenantConfig } from '../../services/tenantConfigStore';

/**
 * SSO Launch Handler
 * ──────────────────
 * Endpoint: /sso/launch
 *
 * This is what an HCM system (Workday, SAP SuccessFactors, BambooHR) calls
 * when an employee clicks the "ClearEqual" tile inside their HCM portal.
 * The HCM passes a signed JWT (`?token=<jwt>`) containing the user's email
 * and role claims; ClearEqual validates against the tenant's SSO certificate,
 * creates a session, and drops the user directly into their portal.
 *
 * Production flow
 * ───────────────
 *   1. HCM redirects: GET https://<tenant>.clearequal.com/sso/launch?token=<jwt>
 *   2. Server validates JWT signature against tenant's IdP certificate
 *   3. Server checks email domain is in allowedDomains whitelist
 *   4. Server creates session, sets HttpOnly cookie
 *   5. Server 302 redirects to /employee-portal or /analytics-dashboard based on role
 *
 * UAT flow
 * ────────
 * The same URL contract works, but the JWT is mock-validated client-side.
 * For demos, we accept query params that fake the IdP claims:
 *   /sso/launch?email=alice@acme.com&role=employee&name=Alice
 *
 * If `launchEnabled` is false in tenant config, we refuse the launch.
 */
const SsoLaunchPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { tenantId, branding } = useTenant();
  const [stage, setStage] = useState('validating'); // validating | success | rejected
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const config = getTenantConfig(tenantId);
    const sso = config.sso || {};

    // Step 1: Check launch is enabled
    if (!sso.launchEnabled) {
      setStage('rejected');
      setErrorMessage(
        'SSO launch is not enabled for this tenant. Ask your administrator to enable HCM-launched SSO in the Admin Console → Single Sign-On tab.'
      );
      return;
    }

    // Step 2: Extract claims from JWT or fall back to dev params
    const token = params.get('token');
    const email = params.get('email') || extractEmailFromMockJwt(token);
    const role = params.get('role') || 'employee';
    const name = params.get('name') || (email ? email.split('@')[0] : 'SSO User');

    if (!email) {
      setStage('rejected');
      setErrorMessage(
        'No identity claim received. The HCM system must include either a signed JWT (token=) or development email/role parameters.'
      );
      return;
    }

    // Step 3: Validate email domain against whitelist
    const domain = email.split('@')[1]?.toLowerCase();
    if (sso.allowedDomains && sso.allowedDomains.length > 0) {
      const allowed = sso.allowedDomains.map((d) => d.toLowerCase().replace(/^@/, ''));
      if (!allowed.includes(domain)) {
        setStage('rejected');
        setErrorMessage(
          `Email domain "${domain}" is not permitted for SSO. Allowed domains: ${allowed.join(', ')}.`
        );
        return;
      }
    }

    // Step 4: Create session
    try {
      localStorage.setItem('user', JSON.stringify({
        email,
        role,
        name,
        loginTime: new Date().toISOString(),
        ssoLaunched: true,
        ssoIssuer: sso.entryPoint || 'unknown',
      }));
    } catch (_) {}

    // Step 5: Land in role-appropriate destination
    setStage('success');
    setTimeout(() => {
      switch (role) {
        case 'admin':
        case 'manager':
          navigate('/analytics-dashboard');
          break;
        case 'approver':
          navigate('/approvals');
          break;
        case 'auditor':
          navigate('/auditor-portal');
          break;
        case 'employee':
        default:
          navigate('/employee-portal');
      }
    }, 800);
  }, [tenantId, params, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full text-center">
        {branding.logoUrl && (
          <img
            src={branding.logoUrl}
            alt={branding.displayName}
            className="h-10 mx-auto mb-6 object-contain"
            style={{ maxWidth: '180px' }}
          />
        )}

        {stage === 'validating' && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Loader2" size={24} className="text-primary animate-spin" />
            </div>
            <h1 className="font-semibold text-foreground mb-2">Signing you in via SSO...</h1>
            <p className="text-sm text-muted-foreground">
              Validating identity claim from your HCM system.
            </p>
          </>
        )}

        {stage === 'success' && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={24} className="text-success" />
            </div>
            <h1 className="font-semibold text-foreground mb-2">Welcome</h1>
            <p className="text-sm text-muted-foreground">
              SSO authentication successful. Redirecting...
            </p>
          </>
        )}

        {stage === 'rejected' && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <Icon name="ShieldX" size={24} className="text-destructive" />
            </div>
            <h1 className="font-semibold text-foreground mb-2">SSO Launch Refused</h1>
            <p className="text-sm text-muted-foreground mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-primary hover:underline"
            >
              Sign in with password instead
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Mock JWT decoder for UAT, extracts the `email` claim from the payload
// without verifying the signature. Production must verify against the
// tenant's stored X.509 certificate.
function extractEmailFromMockJwt(token) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded.email || decoded.sub || null;
  } catch {
    return null;
  }
}

export default SsoLaunchPage;
